/**
 * Role-Based Access Control (RBAC) Configuration
 *
 * Dynamic RBAC: Permissions are stored per-user in the database.
 * This file provides helper functions that check the user's permission object.
 * 
 * Permission format: { module: ['view', 'create', 'update', 'delete'] }
 *
 * Portal types: public, citizen, admin, taluka, system
 */

// ── All available permission modules (for UI rendering) ──────────────────
export const PERMISSION_MODULES = [
  { module: 'dashboard',    label: 'Dashboard',       actions: ['view'] },
  { module: 'households',   label: 'Households',      actions: ['view', 'create', 'update', 'delete'] },
  { module: 'citizens',     label: 'Citizens',        actions: ['view', 'create', 'update', 'delete'] },
  { module: 'certificates', label: 'Certificates',    actions: ['view', 'create', 'update', 'delete'] },
  { module: 'services',     label: 'Services & Certificates', actions: ['view', 'create', 'update', 'delete'] },
  { module: 'complaints',   label: 'Complaints',      actions: ['view', 'create', 'update', 'delete'] },
  { module: 'schemes',      label: 'Schemes',         actions: ['view', 'create', 'update', 'delete'] },
  { module: 'gramsabha',    label: 'Gramsabha',       actions: ['view', 'create', 'update', 'delete'] },
  { module: 'finance',      label: 'Finance',         actions: ['view', 'create', 'update', 'delete', 'pay', 'approve', 'export'] },
  { module: 'taxes',        label: 'Taxes (Legacy)',  actions: ['view', 'create', 'update', 'delete'] },
  { module: 'properties',   label: 'Properties',      actions: ['view', 'create', 'update', 'verify', 'archive'] },
  { module: 'tax_configuration', label: 'Tax Configuration', actions: ['view', 'manage', 'approve'] },
  { module: 'tax_assessments', label: 'Tax Assessments', actions: ['view', 'preview', 'generate', 'approve', 'issue', 'adjust', 'cancel'] },
  { module: 'tax_collection', label: 'Tax Collection', actions: ['view', 'collect', 'refund', 'reconcile', 'reprint_receipt'] },
  { module: 'tax_reports',  label: 'Tax Reports',     actions: ['view', 'export'] },
  { module: 'audit_log',    label: 'Audit Log',       actions: ['view', 'export'] },
  { module: 'projects',     label: 'Projects',        actions: ['view', 'create', 'update', 'delete'] },
  { module: 'content',      label: 'Website Content', actions: ['view', 'create', 'update', 'delete'] },
  { module: 'notices',      label: 'Notices',         actions: ['view', 'create', 'update', 'delete'] },
  { module: 'facilities',   label: 'Facilities',      actions: ['view', 'create', 'update', 'delete'] },
  { module: 'businesses',   label: 'Businesses',      actions: ['view', 'create', 'update', 'delete', 'verify', 'archive', 'manage_users'] },
  { module: 'business_compliance', label: 'Business Compliance', actions: ['view', 'manage', 'review', 'inspect', 'export'] },
  { module: 'departments',  label: 'Departments',     actions: ['view', 'create', 'update', 'delete'] },
  { module: 'staff',        label: 'Staff Members',   actions: ['view', 'create', 'update', 'delete'] },
  { module: 'user_approvals', label: 'User Approvals', actions: ['view', 'update'] },
];

export const PORTAL_TYPES = [
  { value: 'public',  label: 'Public Portal' },
  { value: 'citizen', label: 'Citizen Portal' },
  { value: 'admin',   label: 'GP Admin Portal' },
  { value: 'taluka',  label: 'Taluka Portal' },
  { value: 'system',  label: 'System Admin Portal' },
];

// ── Helper functions (dynamic, permission-object based) ──────────────────

/**
 * Get the user's effective permissions object.
 * Reads from user.permissions (stored in localStorage/context).
 */
const getUserPermissions = (user) => {
  if (!user) return {};
  // super_admin always has full access
  if (user.role === 'super_admin') return null; // null = unlimited
  return user.permissions || {};
};

/** Returns true if the user can view a given section */
export const canAccess = (role, section, user = null) => {
  if (!role) return false;
  if (role === 'super_admin') return true;

  // Dynamic: check user's permission object
  const perms = user?.permissions;
  if (perms && typeof perms === 'object') {
    const modulePerms = perms[section];
    return Array.isArray(modulePerms) && modulePerms.includes('view');
  }

  // Fallback for users without permissions object yet (backward compat)
  return !!role && role !== 'citizen';
};

/** Returns true if the user can create/edit/delete in a given section */
export const canWrite = (role, section, user = null) => {
  if (!role) return false;
  if (role === 'super_admin') return true;

  const perms = user?.permissions;
  if (perms && typeof perms === 'object') {
    const modulePerms = perms[section];
    return Array.isArray(modulePerms) && (
      modulePerms.includes('create') || modulePerms.includes('update') || modulePerms.includes('delete')
    );
  }

  // Fallback
  return false;
};

/** Check a specific action on a module */
export const hasPermission = (user, module, action) => {
  if (!user) return false;
  if (user.role === 'super_admin') return true;
  const perms = user.permissions;
  if (!perms || typeof perms !== 'object') return false;
  const modulePerms = perms[module];
  if (!Array.isArray(modulePerms)) return false;
  if (modulePerms.includes(action)) return true;
  return module === 'finance'
    && ['pay', 'approve', 'export'].includes(action)
    && ['view', 'create', 'update', 'delete'].every(a => modulePerms.includes(a));
};

/** Returns true if the role should have ANY access to the admin panel */
export const hasAdminAccess = (role, user = null) => {
  if (!role) return false;
  if (role === 'super_admin') return true;
  if (role === 'citizen') return false;

  // If user has permissions object, check if any module has view
  const perms = user?.permissions;
  if (perms && typeof perms === 'object') {
    return Object.values(perms).some(actions => Array.isArray(actions) && actions.includes('view'));
  }

  return true; // backward compat: non-citizen roles have admin access
};

/** Returns true if the role is a taluka-level role (no specific GP) */
export const isTalukaRole = (role) =>
  ['tahashil_officer', 'sdo_officer'].includes(role);

/** Returns true if the role is a system-level role */
export const isSystemRole = (role) =>
  role === 'super_admin';

/** Friendly role label for display */
export const roleLabel = (role) =>
  (role || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
